import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { Fullscreen, FullscreenExit, Refresh, VolumeOff, VolumeUp } from '@mui/icons-material';
import BrowserPlaylistPlayer from '../../components/player/BrowserPlaylistPlayer';
import type { BrowserPlayerItem, BrowserPlaylistPlayerHandle } from '../../components/player/BrowserPlaylistPlayer';
import playerService from '../../services/playerService';
import type { Media, PlaylistItem, ScheduleEvent, WebPlayerBootstrap, WebPlayerCommand } from '../../types';

type PlayerMode = 'idle' | 'playing' | 'paused' | 'error' | 'loading';

interface SyncPayload {
  enabled?: boolean;
  session_id: number;
  start_at: string;
  server_time?: string;
  preload_deadline_at?: string;
  sync_tolerance_ms?: number;
  drift_correction_threshold_ms?: number;
  loop?: boolean;
}

interface PlaylistTimelineItem {
  item: PlaylistItem;
  index: number;
  startMs: number;
  durationMs: number;
}

interface ActiveSyncState {
  sessionId: number;
  startAtMs: number;
  serverOffsetMs: number;
  thresholdMs: number;
  toleranceMs: number;
  playlistId: number;
  loop: boolean;
  timeline: PlaylistTimelineItem[];
  totalDurationMs: number;
  actualStartedAt?: string;
  maxDriftMs: number;
}

const APP_BUILD_ID = __APP_BUILD_ID__;
const APP_BUILT_AT = __APP_BUILT_AT__;
const MANUAL_SCHEDULE_OVERRIDE_MS = 24 * 60 * 60 * 1000;
const queueKey = (token: string) => `web-player-queue:${token}`;
const screenshotsKey = (token: string) => `web-player-screenshots:${token}`;
const logsKey = (token: string) => `web-player-logs:${token}`;

const readQueue = (token: string) => {
  try {
    return JSON.parse(localStorage.getItem(queueKey(token)) || '[]') as Array<{ type: string; payload: any }>;
  } catch {
    return [];
  }
};

const appendQueue = (token: string, item: { type: string; payload: any }) => {
  localStorage.setItem(queueKey(token), JSON.stringify([...readQueue(token), item].slice(-100)));
};

const createLocalProofId = () => -Date.now();

const readScreenshots = (token: string) => {
  try {
    return JSON.parse(localStorage.getItem(screenshotsKey(token)) || '[]') as Array<{
      filename: string;
      file_size: number;
      quality: number;
      created_at: string;
      base64_data?: string;
      mime_type?: string;
    }>;
  } catch {
    return [];
  }
};

const appendScreenshot = (token: string, item: ReturnType<typeof readScreenshots>[number]) => {
  const key = screenshotsKey(token);
  const nextScreenshots = [item, ...readScreenshots(token)].slice(0, 5);
  try {
    localStorage.setItem(key, JSON.stringify(nextScreenshots));
    return;
  } catch {
    // Large base64 screenshots can exceed WebView/Electron localStorage quota.
  }

  const compactScreenshots = nextScreenshots.map((screenshot, index) => (
    index === 0
      ? screenshot
      : { ...screenshot, base64_data: undefined }
  ));
  try {
    localStorage.setItem(key, JSON.stringify(compactScreenshots));
    return;
  } catch {
    // If the current screenshot is still too large, keep metadata so the command itself succeeds.
  }

  const metadataOnly = {
    ...item,
    base64_data: undefined,
  };
  try {
    localStorage.setItem(key, JSON.stringify([metadataOnly]));
  } catch {
    localStorage.removeItem(key);
  }
};

const screenshotFilename = () => {
  const stamp = new Date().toISOString().replace(/[-:]/g, '').replace(/\..+/, '').replace('T', '_');
  return `screenshot_${stamp}.png`;
};

const readClientLogs = (token: string) => {
  try {
    return JSON.parse(localStorage.getItem(logsKey(token)) || '[]') as Array<{
      timestamp: string;
      level: string;
      message: string;
      source: string;
    }>;
  } catch {
    return [];
  }
};

const appendClientLog = (token: string, level: string, message: string, source = 'web-player') => {
  const entry = { timestamp: new Date().toISOString(), level, message, source };
  localStorage.setItem(logsKey(token), JSON.stringify([entry, ...readClientLogs(token)].slice(0, 1000)));
};

const detectBrowser = (userAgent: string) => {
  const patterns = [
    { name: 'Edge', regex: /Edg\/([\d.]+)/ },
    { name: 'Chrome', regex: /Chrome\/([\d.]+)/ },
    { name: 'Firefox', regex: /Firefox\/([\d.]+)/ },
    { name: 'Safari', regex: /Version\/([\d.]+).*Safari\// },
  ];
  const match = patterns.map((item) => ({ ...item, match: userAgent.match(item.regex) })).find((item) => item.match);
  return {
    browser_name: match?.name || 'Browser',
    browser_version: match?.match?.[1] || undefined,
  };
};

const detectOs = (userAgent: string, platform: string) => {
  const ua = userAgent.toLowerCase();
  if (ua.includes('windows nt 10.0')) return { os_name: 'Windows', os_version: '10/11' };
  if (ua.includes('windows')) return { os_name: 'Windows', os_version: undefined };
  const macMatch = userAgent.match(/Mac OS X ([\d_]+)/);
  if (macMatch) return { os_name: 'macOS', os_version: macMatch[1].replace(/_/g, '.') };
  const androidMatch = userAgent.match(/Android ([\d.]+)/);
  if (androidMatch) return { os_name: 'Android', os_version: androidMatch[1] };
  const iosMatch = userAgent.match(/OS ([\d_]+) like Mac OS X/);
  if (iosMatch) return { os_name: 'iOS', os_version: iosMatch[1].replace(/_/g, '.') };
  if (ua.includes('linux') || platform.toLowerCase().includes('linux')) return { os_name: 'Linux', os_version: undefined };
  return { os_name: platform || 'Unknown OS', os_version: undefined };
};

const collectRuntimeInfo = () => {
  const nav = navigator as Navigator & {
    deviceMemory?: number;
    connection?: {
      effectiveType?: string;
      downlink?: number;
      rtt?: number;
    };
    userAgentData?: {
      platform?: string;
      brands?: Array<{ brand: string; version: string }>;
    };
  };
  const userAgent = navigator.userAgent;
  const platform = nav.userAgentData?.platform || navigator.platform || '';
  const browser = detectBrowser(userAgent);
  const os = detectOs(userAgent, platform);
  const screen_resolution = `${window.screen.width}x${window.screen.height}`;
  const isElectron = userAgent.toLowerCase().includes('electron');

  return {
    app_build_id: APP_BUILD_ID,
    app_built_at: APP_BUILT_AT,
    user_agent: userAgent,
    player: 'web-player',
    player_kind: 'web_browser',
    client_kind: isElectron ? 'electron_web_player' : 'browser_web_player',
    client_label: isElectron ? 'Windows Electron' : 'Browser Web',
    ...browser,
    ...os,
    platform,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    online: navigator.onLine,
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    screen_resolution,
    device_pixel_ratio: window.devicePixelRatio,
    hardware_concurrency: navigator.hardwareConcurrency,
    device_memory_gb: nav.deviceMemory,
    connection_type: nav.connection?.effectiveType,
    connection_downlink_mbps: nav.connection?.downlink,
    connection_rtt_ms: nav.connection?.rtt,
    touch_points: navigator.maxTouchPoints,
  };
};

const collectBrowserCacheStatus = async () => {
  if (!('caches' in window)) {
    return {
      source: 'web-player',
      supported: false,
      updated_at: new Date().toISOString(),
      total_entries: 0,
      media_entries: 0,
      total_size_bytes: 0,
      cached_media_ids: [],
      items: [],
    };
  }

  try {
    const cache = await caches.open('web-player-media-v1');
    const requests = await cache.keys();
    const items = await Promise.all(
      requests.slice(0, 100).map(async (request) => {
        const response = await cache.match(request);
        const url = new URL(request.url);
        const mediaMatch = url.pathname.match(/\/media\/(\d+)(?:$|[/?#])/);
        const contentLength = Number(response?.headers.get('content-length') || 0);
        let size = Number.isFinite(contentLength) ? contentLength : 0;
        if (!size && response) {
          try {
            size = (await response.clone().blob()).size;
          } catch {
            size = 0;
          }
        }
        return {
          media_id: mediaMatch ? Number(mediaMatch[1]) : undefined,
          filename: url.pathname.split('/').pop() || request.url,
          size_bytes: size,
          cached: Boolean(response),
        };
      }),
    );
    const mediaItems = items.filter((item) => item.media_id);

    return {
      source: 'web-player',
      supported: true,
      updated_at: new Date().toISOString(),
      total_entries: requests.length,
      media_entries: mediaItems.length,
      total_size_bytes: items.reduce((sum, item) => sum + (item.size_bytes || 0), 0),
      cached_media_ids: mediaItems.map((item) => item.media_id).filter(Boolean),
      items: mediaItems,
    };
  } catch (error: any) {
    return {
      source: 'web-player',
      supported: true,
      updated_at: new Date().toISOString(),
      error: error?.message || 'Cache status unavailable',
      total_entries: 0,
      media_entries: 0,
      total_size_bytes: 0,
      cached_media_ids: [],
      items: [],
    };
  }
};

const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) return null;
  const registration = await navigator.serviceWorker.register('/web-player-sw.js');
  return registration.active || registration.waiting || registration.installing;
};

const fetchLatestBuildId = async () => {
  const response = await fetch(`/app-version.json?t=${Date.now()}`, {
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
    },
  });
  if (!response.ok) return null;
  const data = (await response.json()) as { buildId?: string };
  return data.buildId || null;
};

const reloadWithBuildId = (buildId: string) => {
  const url = new URL(window.location.href);
  url.searchParams.set('appBuild', buildId);
  window.location.replace(url.toString());
};

const isActiveNow = (event: ScheduleEvent) => {
  const now = Date.now();
  return new Date(event.start_time).getTime() <= now && now <= new Date(event.end_time).getTime();
};

const isPrefetchableEvent = (event: ScheduleEvent) => {
  const now = Date.now();
  const lookaheadMs = 24 * 60 * 60 * 1000;
  const startsAt = new Date(event.start_time).getTime();
  const endsAt = new Date(event.end_time).getTime();
  return endsAt >= now && startsAt <= now + lookaheadMs;
};

const isPlayableItem = (item: PlaylistItem | undefined): item is PlaylistItem & { media: NonNullable<PlaylistItem['media']> } =>
  Boolean(item?.media && item.is_playable !== false && item.media.is_playable !== false);

const playlistSignature = (items: PlaylistItem[]) =>
  items
    .map(
      (item) =>
        [
          item.id,
          item.media_id,
          item.order_index,
          item.custom_duration || '',
          item.start_time || '',
          item.end_time || '',
          item.volume || '',
          item.fit_mode || '',
          item.is_playable === false ? '0' : '1',
          item.media?.is_playable === false ? '0' : '1',
        ].join(':'),
    )
    .join('|');

const normalizeMedia = (media: Media): Media => ({
  ...media,
  type: media.media_type || media.type,
});

const itemDurationSeconds = (item: PlaylistItem) => {
  if (item.custom_duration && item.custom_duration > 0) return item.custom_duration;
  if (item.start_time !== undefined && item.start_time !== null && item.end_time !== undefined && item.end_time !== null) {
    return Math.max(0, Number(item.end_time) - Number(item.start_time));
  }
  if (item.media?.duration && item.media.duration > 0) return item.media.duration;
  const mediaType = String(item.media?.media_type || item.media?.type || '').toLowerCase();
  if (mediaType === 'image') return 10;
  return 0;
};

const buildTimeline = (items: PlaylistItem[]) => {
  let cursor = 0;
  const timeline = items.filter(isPlayableItem).sort((a, b) => a.order_index - b.order_index).map((item, index) => {
    const durationMs = Math.max(1000, Math.round(itemDurationSeconds(item) * 1000));
    const entry = { item, index, startMs: cursor, durationMs };
    cursor += durationMs;
    return entry;
  });
  return { timeline, totalDurationMs: cursor };
};

const findTimelineItem = (timeline: PlaylistTimelineItem[], totalDurationMs: number, elapsedMs: number, loop: boolean) => {
  if (!timeline.length || totalDurationMs <= 0) return null;
  const positionMs = loop ? ((elapsedMs % totalDurationMs) + totalDurationMs) % totalDurationMs : Math.min(elapsedMs, totalDurationMs - 1);
  const entry = timeline.find((candidate) => positionMs >= candidate.startMs && positionMs < candidate.startMs + candidate.durationMs) || timeline[0];
  return { entry, positionMs, offsetMs: Math.max(0, positionMs - entry.startMs) };
};

const WebBrowserPlayerPage: React.FC = () => {
  const { token = '' } = useParams();
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<BrowserPlaylistPlayerHandle | null>(null);
  const proofIdRef = useRef<number | null>(null);
  const activeItemRef = useRef<BrowserPlayerItem | null>(null);
  const controlsHideTimerRef = useRef<number | null>(null);
  const syncStartTimerRef = useRef<number | null>(null);
  const activeSyncRef = useRef<ActiveSyncState | null>(null);
  const manualScheduleOverrideUntilRef = useRef(0);
  const commandPollingRef = useRef(false);

  const [bootstrap, setBootstrap] = useState<WebPlayerBootstrap | null>(null);
  const [mode, setMode] = useState<PlayerMode>('loading');
  const [muted, setMuted] = useState(true);
  const [volume, setVolume] = useState(50);
  const [activeItem, setActiveItem] = useState<BrowserPlayerItem | null>(null);
  const [playlistItems, setPlaylistItems] = useState<PlaylistItem[]>([]);
  const [playlistIndex, setPlaylistIndex] = useState(0);
  const [currentPlaylistId, setCurrentPlaylistId] = useState<number | null>(null);
  const [currentScheduleEventId, setCurrentScheduleEventId] = useState<number | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [message, setMessage] = useState('Подключение к серверу');
  const [activeSync, setActiveSync] = useState<ActiveSyncState | null>(null);

  useEffect(() => {
    activeItemRef.current = activeItem;
  }, [activeItem]);

  useEffect(() => {
    activeSyncRef.current = activeSync;
  }, [activeSync]);

  const currentMediaId = activeItem?.media.id || null;

  const enableManualScheduleOverride = useCallback(() => {
    manualScheduleOverrideUntilRef.current = Date.now() + MANUAL_SCHEDULE_OVERRIDE_MS;
  }, []);

  const clearManualScheduleOverride = useCallback(() => {
    manualScheduleOverrideUntilRef.current = 0;
  }, []);

  const finishProof = useCallback(
    async (completed: boolean, interrupted = false) => {
      const proofId = proofIdRef.current;
      if (!token || !proofId) return;

      proofIdRef.current = null;
      const payload = {
        completed,
        interrupted,
        duration_actual: Math.round(playerRef.current?.getCurrentTime() || 0),
        playback_data: {
          media_id: activeItemRef.current?.media.id,
          mode,
          sync_session_id: activeSyncRef.current?.sessionId,
          sync_max_drift_ms: activeSyncRef.current?.maxDriftMs,
        },
      };

      if (proofId < 0) {
        appendQueue(token, { type: 'proof_finish', payload: { localId: proofId, data: payload } });
        return;
      }

      try {
        await playerService.proofFinish(token, proofId, payload);
      } catch {
        appendQueue(token, { type: 'proof_finish', payload: { proofId, data: payload } });
      }
    },
    [mode, token],
  );

  const startMedia = useCallback(
    async (
      media: Media,
      playlistItem?: PlaylistItem | null,
      playlistId?: number | null,
      scheduleEventId?: number | null,
      playbackOptions?: { initialSeekSeconds?: number; displayDurationSeconds?: number },
    ) => {
      await finishProof(false, true);
      const normalized = normalizeMedia(media);
      const src = playerService.mediaUrl(token, normalized.id);
      const nextItem = {
        media: normalized,
        playlistItem: playlistItem || null,
        src,
        initialSeekSeconds: playbackOptions?.initialSeekSeconds,
        displayDurationSeconds: playbackOptions?.displayDurationSeconds,
      };

      setActiveItem(nextItem);
      setCurrentPlaylistId(playlistId || null);
      setCurrentScheduleEventId(scheduleEventId || null);
      setMode('playing');
      setMessage(normalized.title);

      const proofPayload = {
        media_id: normalized.id,
        playlist_id: playlistId || null,
        schedule_event_id: scheduleEventId || null,
        duration_planned: playlistItem?.custom_duration || normalized.duration || null,
        playback_data: {
          muted,
          volume,
          sync_session_id: activeSyncRef.current?.sessionId,
          sync_expected_started_at: activeSyncRef.current ? new Date(activeSyncRef.current.startAtMs).toISOString() : undefined,
          sync_actual_started_at: activeSyncRef.current?.actualStartedAt,
          sync_initial_drift_ms: activeSyncRef.current ? Date.now() + activeSyncRef.current.serverOffsetMs - activeSyncRef.current.startAtMs : undefined,
        },
      };

      try {
        const proof = await playerService.proofStart(token, {
          ...proofPayload,
        });
        proofIdRef.current = proof.id;
      } catch {
        const localId = createLocalProofId();
        proofIdRef.current = localId;
        appendQueue(token, {
          type: 'proof_start',
          payload: { localId, data: proofPayload },
        });
      }
    },
    [finishProof, muted, token, volume],
  );

  const playPlaylistItem = useCallback(
    async (items: PlaylistItem[], index: number, playlistId: number, scheduleEventId?: number | null) => {
      if (!items.length) {
        setMode('idle');
        setMessage('В плейлисте нет элементов для воспроизведения');
        return;
      }

      let item: PlaylistItem | undefined;
      let playableIndex = index;
      for (let offset = 0; offset < items.length; offset += 1) {
        const candidateIndex = (index + offset) % items.length;
        const candidate = items[candidateIndex];
        if (isPlayableItem(candidate)) {
          item = candidate;
          playableIndex = candidateIndex;
          break;
        }
      }

      if (!item?.media) {
        setPlaylistItems(items);
        setCurrentPlaylistId(playlistId);
        setMode('idle');
        setMessage('В плейлисте нет доступных файлов для воспроизведения');
        return;
      }

      setPlaylistItems(items);
      setPlaylistIndex(playableIndex);
      setCurrentPlaylistId(playlistId);
      await startMedia(item.media, item, playlistId, scheduleEventId || null);
    },
    [startMedia],
  );

  const prefetchMediaIds = useCallback(
    async (mediaIds: number[]) => {
      if (!token || !('caches' in window)) return;
      const urls = [...new Set(mediaIds)].filter(Boolean).map((mediaId) => playerService.mediaUrl(token, mediaId));
      if (!urls.length) return;

      const worker = await registerServiceWorker();
      if (worker) {
        worker.postMessage({ type: 'PREFETCH_MEDIA', urls });
        return;
      }

      const cache = await caches.open('web-player-media-v1');
      await Promise.allSettled(
        urls.map(async (url) => {
          const request = new Request(url, { credentials: 'same-origin' });
          const cached = await cache.match(request);
          if (!cached) {
            const response = await fetch(request);
            if (response.ok) await cache.put(request, response.clone());
          }
        }),
      );
    },
    [token],
  );

  const playSyncedPosition = useCallback(
    async (syncState: ActiveSyncState) => {
      const nowServerMs = Date.now() + syncState.serverOffsetMs;
      const elapsedMs = Math.max(0, nowServerMs - syncState.startAtMs);
      const found = findTimelineItem(syncState.timeline, syncState.totalDurationMs, elapsedMs, syncState.loop);
      if (!found?.entry.item.media) {
        setMode('idle');
        setMessage('В sync playlist нет доступных файлов');
        return;
      }

      const item = found.entry.item;
      const mediaType = String(item.media.media_type || item.media.type || '').toLowerCase();
      const startTime = Number(item.start_time || 0);
      const initialSeekSeconds = mediaType === 'video' || mediaType === 'audio'
        ? startTime + found.offsetMs / 1000
        : undefined;
      const displayDurationSeconds = mediaType === 'image'
        ? Math.max(1, (found.entry.durationMs - found.offsetMs) / 1000)
        : undefined;

      setPlaylistIndex(found.entry.index);
      await startMedia(item.media, item, syncState.playlistId, currentScheduleEventId, {
        initialSeekSeconds,
        displayDurationSeconds,
      });
      const actualStartedAt = new Date().toISOString();
      const nextState = { ...syncState, actualStartedAt };
      setActiveSync(nextState);
      activeSyncRef.current = nextState;
      await playerService.syncStatus(token, syncState.sessionId, {
        status: 'playing',
        media_id: item.media_id,
        playlist_item_id: item.id,
        timeline_position_ms: Math.round(found.positionMs),
        drift_ms: 0,
        clock_offset_ms: Math.round(syncState.serverOffsetMs),
        buffered: true,
      });
    },
    [currentScheduleEventId, startMedia, token],
  );

  const startSyncedPlaylist = useCallback(
    async (playlistId: number, sync: SyncPayload, scheduleEventId?: number | null) => {
      const response = await playerService.playlistItems(token, playlistId);
      const playable = response.items.filter(isPlayableItem);
      if (!playable.length) {
        setMode('idle');
        setMessage('В sync playlist нет доступных файлов');
        return;
      }
      setPlaylistItems(response.items);
      setCurrentPlaylistId(playlistId);
      setCurrentScheduleEventId(scheduleEventId || null);
      await prefetchMediaIds(playable.map((item) => item.media_id));

      const { timeline, totalDurationMs } = buildTimeline(response.items);
      const serverTimeMs = sync.server_time ? new Date(sync.server_time).getTime() : Date.now();
      const serverOffsetMs = Number.isFinite(serverTimeMs) ? serverTimeMs - Date.now() : 0;
      const startAtMs = new Date(sync.start_at).getTime();
      const syncState: ActiveSyncState = {
        sessionId: Number(sync.session_id),
        startAtMs,
        serverOffsetMs,
        thresholdMs: sync.drift_correction_threshold_ms || 500,
        toleranceMs: sync.sync_tolerance_ms || 250,
        playlistId,
        loop: sync.loop !== false,
        timeline,
        totalDurationMs,
        maxDriftMs: 0,
      };
      setActiveSync(syncState);
      activeSyncRef.current = syncState;
      setMode('loading');
      setMessage(`Синхронный старт: ${new Date(startAtMs).toLocaleTimeString()}`);

      await playerService.syncStatus(token, syncState.sessionId, {
        status: 'preloading',
        clock_offset_ms: Math.round(serverOffsetMs),
        buffered: true,
      });

      if (syncStartTimerRef.current) window.clearTimeout(syncStartTimerRef.current);
      const delayMs = Math.max(0, startAtMs - (Date.now() + serverOffsetMs));
      syncStartTimerRef.current = window.setTimeout(() => {
        playSyncedPosition(syncState).catch((error) => {
          setMode('error');
          setMessage(error?.message || 'Ошибка sync старта');
        });
      }, delayMs);
    },
    [playSyncedPosition, prefetchMediaIds, token],
  );

  const refreshActivePlaylist = useCallback(async () => {
    if (!token || !currentPlaylistId) return;
    const response = await playerService.playlistItems(token, currentPlaylistId);
    const nextItems = response.items;

    setPlaylistItems((previousItems) => {
      if (playlistSignature(previousItems) === playlistSignature(nextItems)) {
        return previousItems;
      }

      const activePlaylistItemId = activeItemRef.current?.playlistItem?.id;
      const activeMediaId = activeItemRef.current?.media.id;
      const nextActiveIndex = nextItems.findIndex((item) => {
        if (activePlaylistItemId && item.id === activePlaylistItemId) return true;
        return Boolean(activeMediaId && item.media_id === activeMediaId);
      });
      setPlaylistIndex((currentIndex) => {
        if (nextActiveIndex >= 0) return nextActiveIndex;
        return Math.min(currentIndex, Math.max(0, nextItems.length - 1));
      });

      return nextItems;
    });

    prefetchMediaIds(nextItems.filter(isPlayableItem).map((item) => item.media_id)).catch(() => undefined);
  }, [currentPlaylistId, prefetchMediaIds, token]);

  const executeCommand = useCallback(
    async (command: WebPlayerCommand) => {
      try {
        const data = command.command_data || {};
        let result: Record<string, any> = { action: command.command_type };

        if (command.command_type === 'play_media') {
          if (data.force) enableManualScheduleOverride();
          const mediaId = Number(data.media_id);
          const media = await playerService.mediaInfo(token, mediaId);
          await startMedia(media, null, null, null);
        } else if (command.command_type === 'play_playlist') {
          if (data.force) enableManualScheduleOverride();
          const playlistId = Number(data.playlist_id);
          if (data.sync?.enabled && data.sync?.session_id && data.sync?.start_at) {
            await startSyncedPlaylist(playlistId, data.sync as SyncPayload, data.schedule_event_id || null);
          } else {
            setActiveSync(null);
            activeSyncRef.current = null;
            const response = await playerService.playlistItems(token, playlistId);
            await playPlaylistItem(response.items, 0, playlistId, null);
          }
        } else if (command.command_type === 'play') {
          setMode('playing');
          await playerRef.current?.play();
        } else if (command.command_type === 'pause') {
          setMode('paused');
          playerRef.current?.pause();
        } else if (command.command_type === 'stop') {
          if (data.force) {
            enableManualScheduleOverride();
          } else {
            clearManualScheduleOverride();
          }
          await finishProof(false, true);
          playerRef.current?.stop();
          setActiveItem(null);
          setCurrentPlaylistId(null);
          setCurrentScheduleEventId(null);
          setActiveSync(null);
          activeSyncRef.current = null;
          if (syncStartTimerRef.current) window.clearTimeout(syncStartTimerRef.current);
          setMode('idle');
        } else if (command.command_type === 'seek') {
          playerRef.current?.seek(Number(data.position || 0));
        } else if (command.command_type === 'set_volume') {
          setVolume(Math.max(0, Math.min(100, Number(data.volume ?? 50))));
        } else if (command.command_type === 'mute') {
          setMuted(data.mute !== false);
        } else if (command.command_type === 'unmute') {
          setMuted(false);
        } else if (command.command_type === 'next') {
          const nextIndex = playlistItems.length ? (playlistIndex + 1) % playlistItems.length : playlistIndex;
          if (currentPlaylistId && playlistItems.length) await playPlaylistItem(playlistItems, nextIndex, currentPlaylistId);
        } else if (command.command_type === 'previous') {
          const previousIndex = playlistItems.length
            ? (playlistIndex - 1 + playlistItems.length) % playlistItems.length
            : playlistIndex;
          if (currentPlaylistId && playlistItems.length) await playPlaylistItem(playlistItems, previousIndex, currentPlaylistId);
        } else if (command.command_type === 'screenshot') {
          const capture = await playerRef.current?.captureScreenshot({
            width: data.width ? Number(data.width) : undefined,
            height: data.height ? Number(data.height) : undefined,
            quality: data.quality ? Number(data.quality) : undefined,
          });
          if (!capture) throw new Error('Плеер не готов к созданию скриншота');
          const screenshot = {
            status: 'success',
            message: 'Снимок экрана создан',
            filename: screenshotFilename(),
            file_size: capture.file_size,
            quality: Number(data.quality || 80),
            created_at: new Date().toISOString(),
            base64_data: data.return_base64 === false ? undefined : capture.base64_data,
            mime_type: capture.mime_type,
          };
          appendScreenshot(token, screenshot);
          result = screenshot;
        } else if (command.command_type === 'get_screenshots') {
          const limit = Number(data.limit || 10);
          const includeData = data.include_data !== false;
          result = {
            status: 'success',
            message: 'Список снимков получен',
            screenshots: readScreenshots(token).slice(0, limit).map((screenshot) => {
              if (includeData) return screenshot;
              const { base64_data: _base64Data, mime_type: _mimeType, ...metadata } = screenshot;
              return metadata;
            }),
            total_count: readScreenshots(token).length,
          };
        } else if (command.command_type === 'get_logs') {
          const limit = Number(data.lines || 100);
          const logType = String(data.log_type || 'all');
          appendClientLog(token, 'INFO', `Admin requested ${logType} logs (${limit} lines)`);
          const entries = readClientLogs(token)
            .filter((entry) => logType === 'all' || entry.source === logType || entry.source === 'web-player')
            .slice(0, limit);
          result = {
            status: 'success',
            message: `Collected ${entries.length} browser player log entries`,
            entries,
            total_lines: entries.length,
          };
        } else if (command.command_type === 'clear_logs') {
          localStorage.removeItem(logsKey(token));
          result = {
            status: 'success',
            message: 'Browser player logs cleared',
            entries: [],
            total_lines: 0,
          };
        }

        appendClientLog(token, 'INFO', `Command executed: ${command.command_type}`);
        await playerService.sendCommandResult(token, command.id, {
          status: 'executed',
          result,
        });
      } catch (error: any) {
        appendClientLog(token, 'ERROR', `Command failed: ${command.command_type}: ${error?.message || 'Command failed'}`);
        await playerService.sendCommandResult(token, command.id, {
          status: 'failed',
          error_message: error?.message || 'Command failed',
        });
        setMessage(error?.message || 'Ошибка выполнения команды');
      }
    },
    [
      clearManualScheduleOverride,
      currentPlaylistId,
      enableManualScheduleOverride,
      finishProof,
      playPlaylistItem,
      playlistIndex,
      playlistItems,
      startMedia,
      startSyncedPlaylist,
      token,
    ],
  );

  const handleEnded = useCallback(async () => {
    await finishProof(true, false);
    if (activeSyncRef.current) {
      await playSyncedPosition(activeSyncRef.current);
      return;
    }
    if (currentPlaylistId && playlistItems.length) {
      const nextIndex = playlistIndex + 1;
      if (nextIndex < playlistItems.length) {
        await playPlaylistItem(playlistItems, nextIndex, currentPlaylistId, currentScheduleEventId);
      } else {
        await playPlaylistItem(playlistItems, 0, currentPlaylistId, currentScheduleEventId);
      }
      return;
    }
    setActiveItem(null);
    setMode('idle');
  }, [currentPlaylistId, currentScheduleEventId, finishProof, playPlaylistItem, playSyncedPosition, playlistIndex, playlistItems]);

  const flushQueue = useCallback(async () => {
    if (!token || !navigator.onLine) return;
    const queued = readQueue(token);
    if (!queued.length) return;

    const remaining: typeof queued = [];
    const proofIdMap = new Map<number, number>();
    for (const item of queued) {
      try {
        if (item.type === 'heartbeat') await playerService.heartbeat(token, item.payload);
        if (item.type === 'sync_status') await playerService.syncStatus(token, item.payload.sessionId, item.payload.data);
        if (item.type === 'proof_start') {
          const proof = await playerService.proofStart(token, item.payload.data || item.payload);
          if (item.payload.localId) {
            proofIdMap.set(item.payload.localId, proof.id);
          }
        }
        if (item.type === 'proof_finish') {
          const proofId = item.payload.proofId || proofIdMap.get(item.payload.localId);
          if (!proofId) {
            remaining.push(item);
            continue;
          }
          await playerService.proofFinish(token, proofId, item.payload.data);
        }
      } catch {
        remaining.push(item);
      }
    }
    localStorage.setItem(queueKey(token), JSON.stringify(remaining));
  }, [token]);

  const sendHeartbeat = useCallback(async () => {
    if (!token) return;
    const runtimeInfo = collectRuntimeInfo();
    const cacheStatus = await collectBrowserCacheStatus();
    const payload = {
      status: 'online',
      playback_status: mode === 'playing' ? 'playing' : mode === 'paused' ? 'paused' : 'stopped',
      current_media_id: currentMediaId,
      current_playlist_id: currentPlaylistId,
      current_schedule_event_id: currentScheduleEventId,
      screen_resolution: runtimeInfo.screen_resolution,
      system_info: {
        ...runtimeInfo,
        cache_status: cacheStatus,
      },
    };
    try {
      await playerService.heartbeat(token, payload);
      await flushQueue();
    } catch {
      appendQueue(token, { type: 'heartbeat', payload });
    }
  }, [currentMediaId, currentPlaylistId, currentScheduleEventId, flushQueue, mode, token]);

  const prefetchAssignedContent = useCallback(async () => {
    if (!token || !('caches' in window)) return;

    const mediaIds = new Set<number>();
    const [mediaResult, playlistsResult] = await Promise.allSettled([
      playerService.assignedMedia(token),
      playerService.assignedPlaylists(token),
    ]);

    if (mediaResult.status === 'fulfilled') {
      mediaResult.value.items.forEach((media) => {
        if (media.is_playable !== false) mediaIds.add(media.id);
      });
    }

    if (playlistsResult.status === 'fulfilled') {
      const playlistItems = await Promise.allSettled(
        playlistsResult.value.items.map((playlist) => playerService.playlistItems(token, playlist.id)),
      );
      playlistItems.forEach((result) => {
        if (result.status === 'fulfilled') {
          result.value.items.forEach((item) => {
            if (isPlayableItem(item)) mediaIds.add(item.media_id);
          });
        }
      });
    }

    const scheduleResult = await Promise.allSettled([playerService.scheduleEvents(token)]);
    const scheduledEvents = scheduleResult[0].status === 'fulfilled' ? scheduleResult[0].value.filter(isPrefetchableEvent) : [];
    scheduledEvents.forEach((event) => {
      if (event.media_id) mediaIds.add(event.media_id);
    });

    const scheduledPlaylistIds = [...new Set(scheduledEvents.map((event) => event.playlist_id).filter(Boolean) as number[])];
    const scheduledPlaylistItems = await Promise.allSettled(
      scheduledPlaylistIds.map((playlistId) => playerService.playlistItems(token, playlistId)),
    );
    scheduledPlaylistItems.forEach((result) => {
      if (result.status === 'fulfilled') {
        result.value.items.forEach((item) => {
          if (isPlayableItem(item)) mediaIds.add(item.media_id);
        });
      }
    });

    const urls = [...mediaIds].map((mediaId) => playerService.mediaUrl(token, mediaId));
    if (!urls.length) return;

    const worker = await registerServiceWorker();
    if (worker) {
      worker.postMessage({ type: 'PREFETCH_MEDIA', urls });
      return;
    }

    const cache = await caches.open('web-player-media-v1');
    await Promise.allSettled(
      urls.map(async (url) => {
        const request = new Request(url, { credentials: 'same-origin' });
        const cached = await cache.match(request);
        if (!cached) {
          const response = await fetch(request);
          if (response.ok) await cache.put(request, response.clone());
        }
      }),
    );
  }, [token]);

  const checkSchedule = useCallback(async () => {
    if (!token) return;
    const events = await playerService.scheduleEvents(token);
    if (manualScheduleOverrideUntilRef.current > Date.now()) {
      return;
    }
    manualScheduleOverrideUntilRef.current = 0;
    const active = [...events].filter(isActiveNow).sort((a, b) => Number(b.priority) - Number(a.priority))[0];
    if (!active || active.id === currentScheduleEventId) return;
    if (active.playback_settings?.sync_playback) {
      setMessage('Ожидание синхронной команды расписания');
      return;
    }

    if (active.media_id) {
      const media = await playerService.mediaInfo(token, active.media_id);
      await startMedia(media, null, active.playlist_id || null, active.id);
    } else if (active.playlist_id) {
      const response = await playerService.playlistItems(token, active.playlist_id);
      await playPlaylistItem(response.items, 0, active.playlist_id, active.id);
    }
  }, [currentScheduleEventId, playPlaylistItem, startMedia, token]);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        const data = await playerService.bootstrap(token);
        if (cancelled) return;
        setBootstrap(data);
        setMode('idle');
        setMessage('Ожидание команды или расписания');
        registerServiceWorker().catch(() => undefined);
      } catch {
        if (!cancelled) {
          setMode('error');
          setMessage('Ссылка недействительна, отозвана или сервер недоступен');
        }
      }
    };

    init();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (bootstrap) {
      checkSchedule().catch(() => undefined);
      prefetchAssignedContent().catch(() => undefined);
    }
  }, [bootstrap, checkSchedule, prefetchAssignedContent]);

  useEffect(() => {
    if (!bootstrap || !token) return;
    const commandInterval = window.setInterval(async () => {
      if (commandPollingRef.current) return;
      commandPollingRef.current = true;
      try {
        const response = await playerService.pendingCommands(token);
        for (const command of response.commands) {
          await executeCommand(command);
        }
      } catch {
        setMessage('Потеряна связь с сервером, повторное подключение');
      } finally {
        commandPollingRef.current = false;
      }
    }, Math.max(3, bootstrap.command_poll_interval_seconds || 4) * 1000);

    const heartbeatInterval = window.setInterval(sendHeartbeat, Math.max(20, bootstrap.heartbeat_interval_seconds || 25) * 1000);
    const scheduleInterval = window.setInterval(checkSchedule, 30000);
    const playlistRefreshInterval = window.setInterval(() => {
      refreshActivePlaylist().catch(() => undefined);
    }, 60000);
    const prefetchInterval = window.setInterval(() => {
      prefetchAssignedContent().catch(() => undefined);
    }, 5 * 60 * 1000);
    sendHeartbeat();

    return () => {
      window.clearInterval(commandInterval);
      window.clearInterval(heartbeatInterval);
      window.clearInterval(scheduleInterval);
      window.clearInterval(playlistRefreshInterval);
      window.clearInterval(prefetchInterval);
    };
  }, [bootstrap, checkSchedule, executeCommand, prefetchAssignedContent, refreshActivePlaylist, sendHeartbeat, token]);

  useEffect(() => {
    if (!bootstrap || !token) return;
    const syncInterval = window.setInterval(async () => {
      const syncState = activeSyncRef.current;
      const item = activeItemRef.current;
      if (!syncState || !item?.playlistItem || mode !== 'playing') return;

      const nowServerMs = Date.now() + syncState.serverOffsetMs;
      const expectedElapsedMs = Math.max(0, nowServerMs - syncState.startAtMs);
      const expected = findTimelineItem(syncState.timeline, syncState.totalDurationMs, expectedElapsedMs, syncState.loop);
      const currentEntry = syncState.timeline.find((entry) => entry.item.id === item.playlistItem?.id);
      if (!expected || !currentEntry) return;

      const mediaType = String(item.media.media_type || item.media.type || '').toLowerCase();
      const actualOffsetMs = mediaType === 'image'
        ? expected.offsetMs
        : Math.max(0, (playerRef.current?.getCurrentTime() || 0) - Number(item.playlistItem.start_time || 0)) * 1000;
      const actualPositionMs = currentEntry.startMs + actualOffsetMs;
      const driftMs = Math.round(actualPositionMs - expected.positionMs);
      const maxDriftMs = Math.max(syncState.maxDriftMs || 0, Math.abs(driftMs));
      const nextState = { ...syncState, maxDriftMs };
      setActiveSync(nextState);
      activeSyncRef.current = nextState;

      if (Math.abs(driftMs) > syncState.thresholdMs) {
        if (expected.entry.item.id !== item.playlistItem.id) {
          await playSyncedPosition(syncState);
          return;
        }
        if (mediaType === 'video' || mediaType === 'audio') {
          playerRef.current?.seek(Number(expected.entry.item.start_time || 0) + expected.offsetMs / 1000);
        }
      }

      try {
        await playerService.syncStatus(token, syncState.sessionId, {
          status: Math.abs(driftMs) > syncState.toleranceMs ? 'drift' : 'playing',
          media_id: item.media.id,
          playlist_item_id: item.playlistItem.id,
          timeline_position_ms: Math.round(actualPositionMs),
          drift_ms: driftMs,
          clock_offset_ms: Math.round(syncState.serverOffsetMs),
          buffered: true,
        });
      } catch {
        appendQueue(token, {
          type: 'sync_status',
          payload: {
            sessionId: syncState.sessionId,
            data: {
              status: 'drift',
              media_id: item.media.id,
              playlist_item_id: item.playlistItem.id,
              timeline_position_ms: Math.round(actualPositionMs),
              drift_ms: driftMs,
              clock_offset_ms: Math.round(syncState.serverOffsetMs),
              buffered: true,
            },
          },
        });
      }
    }, 5000);

    return () => window.clearInterval(syncInterval);
  }, [bootstrap, mode, playSyncedPosition, token]);

  useEffect(() => {
    if (!bootstrap) return;

    let cancelled = false;
    const checkVersion = async () => {
      try {
        const latestBuildId = await fetchLatestBuildId();
        if (!cancelled && latestBuildId && latestBuildId !== APP_BUILD_ID) {
          setMessage('Доступна новая версия плеера, обновление');
          reloadWithBuildId(latestBuildId);
        }
      } catch {
        // Version polling must never interrupt playback on transient network failures.
      }
    };

    const initialTimeout = window.setTimeout(checkVersion, 15000);
    const versionInterval = window.setInterval(checkVersion, 60000);

    return () => {
      cancelled = true;
      window.clearTimeout(initialTimeout);
      window.clearInterval(versionInterval);
    };
  }, [bootstrap]);

  useEffect(() => {
    const updateFullscreenState = () => {
      const fullscreenElement =
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).msFullscreenElement;
      setIsFullscreen(Boolean(fullscreenElement));
    };

    document.addEventListener('fullscreenchange', updateFullscreenState);
    document.addEventListener('webkitfullscreenchange', updateFullscreenState as EventListener);
    document.addEventListener('MSFullscreenChange', updateFullscreenState as EventListener);

    return () => {
      document.removeEventListener('fullscreenchange', updateFullscreenState);
      document.removeEventListener('webkitfullscreenchange', updateFullscreenState as EventListener);
      document.removeEventListener('MSFullscreenChange', updateFullscreenState as EventListener);
    };
  }, []);

  const requestFullscreen = async () => {
    const documentWithFullscreen = document as any;
    const target = (surfaceRef.current || document.documentElement) as HTMLElement & {
      webkitRequestFullscreen?: () => Promise<void> | void;
      msRequestFullscreen?: () => Promise<void> | void;
    };

    try {
      if (document.fullscreenElement || documentWithFullscreen.webkitFullscreenElement || documentWithFullscreen.msFullscreenElement) {
        const exit =
          document.exitFullscreen ||
          documentWithFullscreen.webkitExitFullscreen ||
          documentWithFullscreen.msExitFullscreen;
        await exit?.call(document);
        return;
      }

      const enter = target.requestFullscreen || target.webkitRequestFullscreen || target.msRequestFullscreen;
      if (!enter) {
        setMessage('Браузер не поддерживает fullscreen API');
        return;
      }

      await enter.call(target);
    } catch (error: any) {
      setMessage(error?.message || 'Fullscreen доступен только после клика пользователя');
    }
  };

  const showControlsTemporarily = useCallback(() => {
    setControlsVisible(true);
    if (controlsHideTimerRef.current) {
      window.clearTimeout(controlsHideTimerRef.current);
    }
    controlsHideTimerRef.current = window.setTimeout(() => {
      if (mode === 'playing' && activeItemRef.current) {
        setControlsVisible(false);
      }
    }, 4000);
  }, [mode]);

  useEffect(() => {
    if (mode === 'playing' && activeItem) {
      showControlsTemporarily();
      return;
    }

    setControlsVisible(true);
    if (controlsHideTimerRef.current) {
      window.clearTimeout(controlsHideTimerRef.current);
      controlsHideTimerRef.current = null;
    }
  }, [activeItem, mode, showControlsTemporarily]);

  useEffect(
    () => () => {
      if (controlsHideTimerRef.current) {
        window.clearTimeout(controlsHideTimerRef.current);
      }
      if (syncStartTimerRef.current) {
        window.clearTimeout(syncStartTimerRef.current);
      }
    },
    [],
  );

  const statusText = useMemo(() => {
    if (mode === 'loading') return 'Загрузка';
    if (mode === 'error') return 'Ошибка';
    if (mode === 'paused') return 'Пауза';
    if (mode === 'playing') return activeItem?.media.title || 'Воспроизведение';
    return message;
  }, [activeItem, message, mode]);

  return (
    <Box
      ref={surfaceRef}
      tabIndex={0}
      onPointerMove={showControlsTemporarily}
      onTouchStart={showControlsTemporarily}
      onKeyDown={showControlsTemporarily}
      sx={{ width: '100vw', height: '100vh', bgcolor: '#000', color: '#fff', overflow: 'hidden', position: 'relative' }}
    >
      <BrowserPlaylistPlayer
        ref={playerRef}
        item={activeItem}
        playing={mode === 'playing'}
        muted={muted}
        volume={volume}
        onEnded={handleEnded}
        onError={(error) => {
          setMode('error');
          setMessage(error);
        }}
      />

      <Stack
        direction="row"
        spacing={1}
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          bgcolor: 'rgba(0,0,0,0.45)',
          borderRadius: 2,
          p: 1,
          opacity: controlsVisible ? 1 : 0,
          pointerEvents: controlsVisible ? 'auto' : 'none',
          transition: 'opacity 180ms ease',
        }}
      >
        <Button
          size="small"
          variant="contained"
          startIcon={isFullscreen ? <FullscreenExit /> : <Fullscreen />}
          onClick={requestFullscreen}
        >
          {isFullscreen ? 'Exit' : 'Fullscreen'}
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={muted ? <VolumeOff /> : <VolumeUp />}
          onClick={() => setMuted((value) => !value)}
        >
          {muted ? 'Unmute' : 'Mute'}
        </Button>
        <Button size="small" variant="contained" startIcon={<Refresh />} onClick={() => window.location.reload()}>
          Reload
        </Button>
      </Stack>

      {(mode === 'loading' || mode === 'error' || !activeItem) && (
        <Stack
          spacing={2}
          alignItems="center"
          sx={{ position: 'absolute', inset: 0, justifyContent: 'center', pointerEvents: 'none' }}
        >
          {mode === 'loading' && <CircularProgress color="inherit" />}
          <Typography variant="h5" sx={{ textAlign: 'center', maxWidth: 720 }}>
            {statusText}
          </Typography>
          {bootstrap?.device.name && (
            <Typography variant="body2" color="rgba(255,255,255,0.72)">
              {bootstrap.device.name}
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default WebBrowserPlayerPage;
